import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ChevronDown,
  Package,
  Menu,
  X,
} from "lucide-react";
import BrainMark from "@/components/BrainMark";
import { AppleIcon, WindowsIcon } from "@/components/PlatformIcons";
import { DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL } from "@/lib/cta";
import { cn } from "@/lib/utils";
import { SiteSearch } from "./SiteSearch";

const PRODUCTS = [
  {
    name: "سکوی ابری",
    description: "راه‌اندازی پروژه از گیت، ایمیج داکر یا بازارچه",
    href: "/products/paas",
  },
  {
    name: "افزونه‌های مدیریت‌شده",
    description: "دیتابیس، کش، صف و ذخیره‌سازی",
    href: "/products/databases",
  },
  {
    name: "دی‌ان‌اس ابری",
    description: "مدیریت دامنه‌ها و رکوردهای DNS",
    href: "/products/dns",
  },
  {
    name: "ایمیل سازمانی",
    description: "ایمیل اختصاصی روی دامنهٔ شما",
    href: "/products/email",
  },
];

const STACKS = [
  { name: "نود جی‌اس", href: "/products/nodejs" },
  { name: "پایتون", href: "/products/python" },
  { name: "لاراول و پی‌اچ‌پی", href: "/products/laravel" },
  { name: "گو", href: "/products/go" },
  { name: "روبی", href: "/products/ruby" },
  { name: "راست", href: "/products/rust" },
  { name: "ری‌اکت", href: "/products/react" },
  { name: "نکست جی‌اس", href: "/products/nextjs" },
  { name: "ویو", href: "/products/vue" },
  { name: "Svelte", href: "/products/svelte" },
  { name: "Astro", href: "/products/astro" },
  { name: "سایت استاتیک", href: "/products/static-sites" },
];

const DOCS = [
  {
    name: "اپ دسکتاپ گردو",
    description: "استقرار و رفع خطا با گفتگو، برای مک و ویندوز",
    href: "/download",
    external: false,
  },
  {
    name: "راهنماهای استقرار",
    description: "آموزش گام‌به‌گام انتشار پروژه با زبان‌ها و فریم‌ورک‌های مختلف",
    href: "/guides",
    external: false,
  },
  {
    name: "مستندات فنی",
    description: "راهنمای ساخت و مدیریت سرویس‌ها",
    href: "/support/docs",
    external: true,
  },
  {
    name: "ابزار خط فرمان (CLI)",
    description: "انتشار و مدیریت پروژه از ترمینال",
    href: "https://get.gerdoo.cloud",
    external: true,
  },
  {
    name: "وبلاگ",
    description: "آموزش‌ها، مقاله‌ها و تازه‌های گردو",
    href: "/blog",
    external: true,
  },
  {
    name: "مراکز داده",
    description: "زیرساخت و موقعیت سرورها",
    href: "/datacenters",
    external: false,
  },
  {
    name: "قابلیت‌ها",
    description: "آشنایی با امکانات گردو",
    href: "/features",
    external: false,
  },
];

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2" aria-label="گردو - صفحه اصلی">
      <span className="flex h-6 w-6 items-center justify-center rounded-[5px] bg-gerdoo-600">
        <BrainMark className="h-3.5 w-3.5 text-white" />
      </span>
      <span className="text-[13px] font-semibold tracking-tight text-ink-950">گردو</span>
    </Link>
  );
}

type MenuKey = "products" | "developers" | "docs";

const navBtnBase =
  "flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[12px] font-normal transition-colors";

/** A row inside a dropdown — fills with the brand colour on hover. */
const menuItem =
  "group block rounded-md p-2.5 text-start transition-colors hover:bg-gerdoo-500/10";

/** One light bar on every page — the whole site sits on the same warm surface. */
const navBtn = cn(navBtnBase, "text-ink-600 hover:text-ink-950");

export function Navbar() {
  const mobilePanel = "border-t border-black/[0.06] bg-white";
  const mobileLabel = "text-ink-500";
  const mobileItem = "text-ink-800";
  const mobileRule = "bg-black/[0.08]";
  // Bar actions: outlined "ورود", solid dark "شروع رایگان".
  const barAction =
    "inline-flex h-9 shrink-0 items-center justify-center rounded-md px-4 text-[12.5px] font-semibold leading-none no-underline transition-colors";
  const signInAction =
    "border border-black/[0.12] bg-white text-ink-800 hover:border-black/25 hover:text-ink-950";
  const signUpAction = "bg-ink-950 text-white hover:bg-ink-800";
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    if (!isMobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileOpen]);

  return (
    <header data-pagefind-ignore="all" className="fixed inset-x-0 top-0 z-50 border-b border-black/[0.06] bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 w-full max-w-[1024px] items-center justify-between px-4 sm:h-[4.25rem] sm:px-14">
        <div className="flex items-center gap-6">
          <Brand />

          <nav className="hidden items-center gap-0.5 md:flex">
            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("products")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className={navBtn}>
                محصولات
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform duration-200",
                    openMenu === "products" && "rotate-180",
                  )}
                />
              </button>

              {openMenu === "products" && (
                <div className="absolute right-0 top-full pt-2">
                  <div className="w-[520px] rounded-2xl bg-white p-3.5 shadow-xl dark:bg-[#1c1c1e]">
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      {PRODUCTS.map((product) => (
                        <Link key={product.href} to={product.href} className={menuItem}>
                          <span className="block text-[13px] font-semibold text-[#1d1d1f] transition-colors group-hover:text-gerdoo-700 dark:text-[#f5f5f7]">
                            {product.name}
                          </span>
                          <span className="mt-0.5 block text-[11px] leading-snug text-[#6e6e73] transition-colors group-hover:text-gerdoo-700/70 dark:text-[#a1a1a6]">
                            {product.description}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("developers")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className={navBtn}>
                توسعه‌دهندگان
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform duration-200",
                    openMenu === "developers" && "rotate-180",
                  )}
                />
              </button>

              {openMenu === "developers" && (
                <div className="absolute right-0 top-full pt-2">
                  <div className="w-[480px] rounded-2xl bg-white p-3.5 shadow-xl dark:bg-[#1c1c1e]">
                    <p className="mb-2 px-2.5 pt-1 text-[11px] font-semibold text-[#6e6e73] dark:text-[#a1a1a6]">
                      زبان‌ها و فریم‌ورک‌های پشتیبانی‌شده
                    </p>
                    <div className="grid grid-cols-3 gap-x-1 gap-y-0.5">
                      {STACKS.map((stack) => (
                        <Link
                          key={stack.href}
                          to={stack.href}
                          className="rounded-md px-2.5 py-1.5 text-[12.5px] font-medium text-[#1d1d1f] transition-colors hover:bg-gerdoo-500/10 hover:text-gerdoo-700 dark:text-[#f5f5f7]"
                        >
                          {stack.name}
                        </Link>
                      ))}
                    </div>
                    <div className="mt-3 border-t border-black/8 pt-3 dark:border-white/10">
                      <Link
                        to="/marketplace"
                        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] font-semibold text-[#1d1d1f] transition-colors hover:bg-gerdoo-500/10 hover:text-gerdoo-700 dark:text-[#f5f5f7]"
                      >
                        <Package className="h-3.5 w-3.5" />
                        بازارچه سرویس‌ها
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/pricing" className={navBtn}>
              قیمت‌گذاری
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setOpenMenu("docs")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button className={navBtn}>
                مستندات
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform duration-200",
                    openMenu === "docs" && "rotate-180",
                  )}
                />
              </button>

              {openMenu === "docs" && (
                <div className="absolute right-0 top-full pt-2">
                  <div className="w-[440px] rounded-2xl bg-white p-3.5 shadow-xl dark:bg-[#1c1c1e]">
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      {DOCS.map((item) => {
                        const inner = (
                          <>
                            <span className="block text-[13px] font-semibold text-[#1d1d1f] transition-colors group-hover:text-gerdoo-700 dark:text-[#f5f5f7]">
                              {item.name}
                            </span>
                            <span className="mt-0.5 block text-[11px] leading-snug text-[#6e6e73] transition-colors group-hover:text-gerdoo-700/70 dark:text-[#a1a1a6]">
                              {item.description}
                            </span>
                          </>
                        );
                        return item.external ? (
                          <a key={item.href} href={item.href} className={menuItem}>
                            {inner}
                          </a>
                        ) : (
                          <Link key={item.href} to={item.href} className={menuItem}>
                            {inner}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <SiteSearch className="px-2" navigateTo={navigate} />
          {/* Desktop app download: wide screens only — phones reach it from the menu. */}
          <Link
            to="/download"
            className={cn(barAction, signInAction, "hidden gap-2 lg:inline-flex h-9 px-3.5")}
          >
            <span className="flex items-center gap-1.5" aria-hidden>
              <AppleIcon className="h-[18px] w-[18px]" />
              <WindowsIcon className="h-4 w-4" />
            </span>
            دانلود
          </Link>
          <a
            href={DASHBOARD_SIGNIN_URL}
            className={cn(barAction, signInAction, "h-8 px-3 sm:h-9 sm:px-4")}
          >
            ورود
          </a>
          <a href={DASHBOARD_SIGNUP_URL} className={cn(barAction, signUpAction, "h-8 px-3 sm:h-9 sm:px-4")}>
            شروع رایگان
          </a>
          <button
            onClick={() => setIsMobileOpen((v) => !v)}
            aria-label={isMobileOpen ? "بستن منو" : "باز کردن منو"}
            aria-expanded={isMobileOpen}
            className="flex h-8 w-8 items-center justify-center text-ink-700 md:hidden"
          >
            {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className={cn("flex max-h-[calc(100dvh-4rem)] flex-col md:hidden", mobilePanel)}>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
            <p className={cn("px-3 pb-1 pt-2 text-[11px] font-semibold", mobileLabel)}>محصولات</p>
            {PRODUCTS.map((p) => (
              <Link
                key={p.href}
                to={p.href}
                onClick={() => setIsMobileOpen(false)}
                className={cn("block rounded-md px-3 py-2.5 text-[14px] font-medium transition-opacity hover:opacity-70", mobileItem)}
              >
                {p.name}
              </Link>
            ))}

            <div className={cn("my-2 h-px", mobileRule)} />
            <p className={cn("px-3 pb-1 pt-2 text-[11px] font-semibold", mobileLabel)}>توسعه‌دهندگان</p>
            <div className="grid grid-cols-2">
              {STACKS.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  onClick={() => setIsMobileOpen(false)}
                  className={cn("rounded-md px-3 py-2 text-[13px] font-medium transition-opacity hover:opacity-70", mobileItem)}
                >
                  {s.name}
                </Link>
              ))}
            </div>
            <Link
              to="/marketplace"
              onClick={() => setIsMobileOpen(false)}
              className={cn("flex items-center gap-2 rounded-md px-3 py-2.5 text-[14px] font-semibold transition-opacity hover:opacity-70", mobileItem)}
            >
              <Package className="h-4 w-4" />
              بازارچه سرویس‌ها
            </Link>

            <div className={cn("my-2 h-px", mobileRule)} />
            <Link
              to="/pricing"
              onClick={() => setIsMobileOpen(false)}
              className={cn("block rounded-md px-3 py-2.5 text-[14px] font-medium transition-opacity hover:opacity-70", mobileItem)}
            >
              قیمت‌گذاری
            </Link>

            <div className={cn("my-2 h-px", mobileRule)} />
            <p className={cn("px-3 pb-1 pt-2 text-[11px] font-semibold", mobileLabel)}>مستندات</p>
            {DOCS.map((item) =>
              item.external ? (
                <a
                  key={item.href}
                  href={item.href}
                  className={cn("block rounded-md px-3 py-2.5 text-[14px] font-medium transition-opacity hover:opacity-70", mobileItem)}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMobileOpen(false)}
                  className={cn("block rounded-md px-3 py-2.5 text-[14px] font-medium transition-opacity hover:opacity-70", mobileItem)}
                >
                  {item.name}
                </Link>
              ),
            )}
          </div>

          <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-black/[0.06] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
            <a
              href={DASHBOARD_SIGNIN_URL}
              className={cn(barAction, signInAction, "h-10 w-full")}
            >
              ورود
            </a>
            <a href={DASHBOARD_SIGNUP_URL} className={cn(barAction, signUpAction, "h-10 w-full")}>
              شروع رایگان
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
